import { NoResultsSVG } from "src/assets";
import {
  HeadingSubtitleStyled,
  LargeHeadingStyled,
  SectionFiltersHeaderStyled,
  SectionHeaderStyled,
  SmallHeadingStyled,
} from "src/components/custom/texts/texts.styled";
import { FlexStyled } from "src/components/registration/register-introduction/register-introduction.styled";
import { IStudentGrades } from "@/Interfaces/teacherCourses";
import { Box, Icon } from "@nubeteck/components";
import { Button, Card, Flex, Form, Image, Input, Modal, Segmented } from "antd";
import { Content } from "antd/es/layout/layout";
import Table, { ColumnsType } from "antd/es/table";
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { toastErrorStyle, toastSuccessStyle, useSuccessGradesSavedToast } from "src/constants";
import { SegmentedValue } from "antd/es/segmented";
import { ColumnContainer } from "src/components/layout/main-layout/main-layout.styled";
import {
  useGetGradesByStudentMutation,
  useUpdateGradesByStudentMutation,
} from "src/services/gradesServices";
import { useParams } from "react-router-dom";
import { editableComponents } from "src/components/custom/editable-table-items/editable-table-items";
import GradesPDFPreview from "src/components/other/grades-pdf/gradesPdf";
import { ButtonAlternativeStyled } from "src/components/custom/buttons/buttons.styled";

function StudentGrades() {
  const { estudianteId, seccionId } = useParams();
  const [getGrades, { data, isLoading }] = useGetGradesByStudentMutation();
  const [saveGrades, { isLoading: isLoadingSaving, isSuccess, error }] =
    useUpdateGradesByStudentMutation();
  const [tipoCalificacion, setTipoCalificacion] = React.useState<SegmentedValue>(1);
  const [actividadNombre, setActividadNombre] = useState<string>("");
  const [totalAcumulado, setTotalAcumulado] = useState(0);
  const [calificaciones, setCalificaciones] = useState<IStudentGrades[]>([]);
  const [modalOpened, setModalOpened] = useState(false);
  const [isInputTouched, setIsInputTouched] = useState(false);
  const [modal, contextHolder] = Modal.useModal();
  const showSuccessGradesSavedToast = useSuccessGradesSavedToast();

  const obtenerCalificaciones = useCallback(() => {
    getGrades({ estudianteId, seccionId, actividad: actividadNombre });
  }, [estudianteId, seccionId, actividadNombre, getGrades]);

  useEffect(() => {
    if (estudianteId && seccionId) obtenerCalificaciones();
  }, [estudianteId, seccionId, isSuccess, obtenerCalificaciones, tipoCalificacion]);

  useEffect(() => {
    setCalificaciones(data?.calificaciones);
  }, [data]);

  useEffect(() => {
    setTotalAcumulado(data?.totalAcumulado);
  }, [data]);

  useEffect(() => {
    if (error) toast.error(error?.data?.detail, toastErrorStyle);
    if (isSuccess) {
      showSuccessGradesSavedToast();
      setIsInputTouched(false);
    }
  }, [error, isSuccess, showSuccessGradesSavedToast]);

  // useEffect(() => {
  //   if (data?.info) {
  //     const { alumnosActaValidados, estanTodosCalificados } = data.info;
  //     if (alumnosActaValidados === false && estanTodosCalificados === true) {
  //       toast.error(
  //         // Cambiado a error
  //         "No se puede cerrar el acta por inconsistencia en la lista de alumnos.",
  //         toastSuccessStyle,
  //       );
  //     }
  //   }
  // }, [data.info, data.info.alumnosActaValidados]);

  const defaultColumns = React.useMemo<ColumnsType<IStudentGrades>>(
    () => [
      {
        title: "Actividad",
        dataIndex: "actividadNombre",
      },
      {
        title: "Puntos obtenidos",
        dataIndex: "puntosObtenidos",
        editable: tipoCalificacion === 2,
        fieldType: "number",
      },
      {
        title: "Puntos posibles",
        dataIndex: "puntosPosibles",
        render: (value) => `/${value}`,
      },
      {
        title: "Calificación (Escala 0 - 100)",
        dataIndex: "calificacionEscala",
        editable: tipoCalificacion === 1,
        fieldType: "number",
      },
      {
        title: "Retroalimentación",
        dataIndex: "feedback",
        editable: true,
        fieldType: "richText",
      },
    ],

    [tipoCalificacion],
  );

  const handleSave = (row: IStudentGrades) => {
    const tempArr = calificaciones.map((cal) =>
      cal.actividadId !== row?.actividadId
        ? cal
        : { ...row, calificacionEscala: row.calificacionEscala.toString() },
    );
    setCalificaciones(tempArr);
  };

  const confirm = (value: number | string) => {
    modal.confirm({
      title: `¿Seguro(a) que desea cambiar el modo de calificación?`,
      icon: <Icon name="" color="#FFC53D" />,
      content: `Las calificaciones sin guardar se perderán.`,
      okText: "Aceptar",
      onOk: () => {
        setTipoCalificacion(value);
        setIsInputTouched(false);
      },
      cancelText: "Cancelar",
      style: { top: 200 },
    });
  };

  const columns = defaultColumns.map((col: any) => {
    return {
      ...col,
      onCell: (record: IStudentGrades) => ({
        record,
        fieldType: col.fieldType,
        options: col.options,
        onchange: () => setIsInputTouched(true),
        editable:
          col.editable &&
          (!data?.info?.estaCursoFinalizado || col.fieldType === "richText") &&
          data?.info?.estaCursoPreparado,
        max: col.dataIndex == "puntosObtenidos" ? record.puntosPosibles : null,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  return (
    <ColumnContainer>
      <SectionHeaderStyled>
        <div>
          <LargeHeadingStyled>
            Calificaciones del estudiante {data?.info?.nombre}
          </LargeHeadingStyled>
          <HeadingSubtitleStyled>
            Sección: {data?.info?.seccion} - Horario:{" "}
            {data?.info?.horario?.map((hor: string) => hor)}
          </HeadingSubtitleStyled>
        </div>
        <Button onClick={() => history.back()} type="default">
          Volver atrás
        </Button>
      </SectionHeaderStyled>

      <SectionFiltersHeaderStyled style={{ alignItems: "end", flexWrap: "wrap", height: "65px" }}>
        <FlexStyled style={{ gap: 30 }}>
          <Form.Item label="Búsqueda por nombre de la actividad">
            <Input
              allowClear
              value={actividadNombre ?? undefined}
              onChange={(e) => {
                setActividadNombre(e.target.value);
              }}
              placeholder="Búsqueda"
            />
          </Form.Item>
        </FlexStyled>
        <FlexStyled style={{ gap: 15, marginBottom: "20px", flexWrap: "wrap" }}>
          <Segmented
            onChange={(value) => (isInputTouched ? confirm(value) : setTipoCalificacion(value))}
            style={{ backgroundColor: "rgba(0, 0, 0, 0.04)" }}
            value={tipoCalificacion}
            options={[
              {
                value: 1,
                label: "Escala 100",
              },
              {
                value: 2,
                label: "Puntaje",
              },
            ]}
          />
          {seccionId && (
            <Button
              type="primary"
              disabled={data?.info?.estaCursoFinalizado || !data?.info?.estaCursoPreparado}
              loading={isLoading || isLoadingSaving}
              onClick={() => {
                saveGrades({
                  estudianteId,
                  calificaciones,
                  seccionId: +seccionId,
                  isScaled: tipoCalificacion === 1,
                });
              }}
            >
              Guardar
            </Button>
          )}
          <ButtonAlternativeStyled
            disabled={!data?.info?.estanTodosCalificados || data?.info?.estaCursoFinalizado}
            loading={isLoading || isLoadingSaving}
            onClick={() => {
              if (data?.info?.alumnosActaValidados === false && data?.info?.estanTodosCalificados) {
                toast.error(
                  // Cambiado a error
                  "No se puede cerrar el acta por inconsistencia en la lista de alumnos.",
                  toastSuccessStyle,
                );
                return;
              } else setModalOpened(true);
            }}
          >
            Cerrar acta
          </ButtonAlternativeStyled>
        </FlexStyled>
      </SectionFiltersHeaderStyled>
      <Content style={{ marginTop: "16px" }}>
        <Card bodyStyle={{ padding: 0, width: "100%" }}>
          <Table<any>
            components={editableComponents}
            locale={{
              emptyText: (
                <Box>
                  <Box>
                    <SmallHeadingStyled>No se encontraron actividades</SmallHeadingStyled>
                    <Image
                      src={NoResultsSVG}
                      preview={false}
                      alt="Ilustración sin resultados"
                      width={200}
                    />
                  </Box>
                </Box>
              ),
            }}
            loading={isLoading}
            columns={columns}
            dataSource={calificaciones}
            rowKey={(v) => v.estudianteId}
            footer={() => (
              <Flex
                style={{
                  gap: "67%",
                  padding: 10,
                  margin: "5px 0px 0px 0px",
                  color: "#00479B",
                  fontWeight: "bold",
                  backgroundColor: "#F4F6FA",
                }}
              >
                <span>PUNTOS TOTALES:</span>
                <span>{totalAcumulado}</span>
              </Flex>
            )}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              style: { marginRight: "30px" },
            }}
            scroll={{ x: 800 }}
          />
        </Card>
      </Content>
      <GradesPDFPreview
        opened={modalOpened}
        onSuccess={() => obtenerCalificaciones()}
        close={() => setModalOpened(false)}
        seccionId={seccionId}
      />
      {contextHolder}
    </ColumnContainer>
  );
}

export default StudentGrades;
